import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Folder, Flame, Tag } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { blogFetch, blogCategoryPath, blogTagPath } from "@/lib/blog";
import { CategoryChip } from "./CategoryChip";
import { BlogCard } from "./BlogCard";

/**
 * Sidebar: Kategoriler + Popüler Yazılar + Etiket Bulutu.
 * Kategorileri zaten parent'tan alabiliyoruz (liste sayfasında çekiliyor) ama
 * sidebar tek başına da kullanılabilir olsun diye fetch yapıyor.
 */
export function BlogSidebar({ categories: categoriesProp = null, tags = [] }) {
  const { lang, t } = useLanguage();
  const [categories, setCategories] = useState(categoriesProp || []);
  const [popular, setPopular] = useState([]);

  useEffect(() => {
    const ctrl = new AbortController();
    if (!categoriesProp) {
      blogFetch("/categories", { lang, signal: ctrl.signal })
        .then((d) => setCategories(d.categories || []))
        .catch(() => {});
    }
    blogFetch("/popular", { lang, signal: ctrl.signal })
      .then((d) => setPopular(d.posts || []))
      .catch(() => {});
    return () => ctrl.abort();
  }, [lang, categoriesProp]);

  return (
    <aside className="space-y-6">
      {/* Kategoriler */}
      {categories.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-4">
            <Folder className="w-4 h-4 text-[#22A7D1]" />
            {t("blog.categories")}
          </h3>
          <ul className="space-y-1.5">
            {categories.map((cat) => (
              <li key={cat.id}>
                <Link
                  to={blogCategoryPath(lang, cat.slug)}
                  className="flex items-center justify-between text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 -mx-2 px-2 py-1.5 rounded-lg transition-colors group"
                >
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: cat.color }}
                    />
                    {cat.name}
                  </span>
                  <span className="text-xs text-slate-400 group-hover:text-slate-600">
                    {cat.post_count}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Popüler */}
      {popular.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-4">
            <Flame className="w-4 h-4 text-[#f59e0b]" />
            {t("blog.popular")}
          </h3>
          <div className="space-y-1">
            {popular.map((p) => (
              <BlogCard key={p.id} post={p} lang={lang} variant="compact" />
            ))}
          </div>
        </div>
      )}

      {/* Etiketler */}
      {tags.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-4">
            <Tag className="w-4 h-4 text-slate-400" />
            {t("blog.tags")}
          </h3>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <Link
                key={tag}
                to={blogTagPath(lang, tag)}
                className="text-xs px-2.5 py-1 rounded-lg bg-slate-50 text-slate-600 hover:bg-sky-50 hover:text-[#22A7D1] transition-colors"
              >
                #{tag}
              </Link>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}
